import React, { useState } from "react";
import { Alert, Button, Space, Tooltip, Typography } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { listPatientPrescriptionDrafts } from "../../../../../api/healthcare/PrescriptionDraftApi";
import { DataTable, StatusTag } from "../../../../../components/shared";
import { usePrescribingOrganisations } from "../../../../../hooks/healthcare/usePrescribingOrganisations";
import type { Patient } from "../../../../../types/healthcare/PatientTypes";
import type { PrescriptionDraft } from "../../../../../types/healthcare/PrescriptionDraftTypes";
import { DRAFT_NOT_ISSUED_LABEL, isEditableDraft, medicineSummary, modalityLabel } from "../../../../../utils/healthcare/prescriptionDraftDisplay";
import { classifyPrescriptionDraftFailure } from "../../../../../utils/healthcare/prescriptionDraftFailure";
import { canViewPrescriptionDrafts, canWritePrescriptionDrafts } from "../../../../../utils/userPrefs/validatePermissions";
import { formatInZone } from "../../../../../utils/time";
import PrescriptionDraftModal, { prescriptionDraftsKey, type PrescriptionDraftModalMode } from "./PrescriptionDraftModal";

const { Text } = Typography;

interface PrescriptionsTabProps {
	patient: Patient;
}

class ForeignPatientDraftError extends Error {}

const PrescriptionsTab: React.FC<PrescriptionsTabProps> = ({ patient }) => {
	const [modal, setModal] = useState<{ mode: PrescriptionDraftModalMode; draft?: PrescriptionDraft } | null>(null);
	const organisations = usePrescribingOrganisations();
	const mayRead = canViewPrescriptionDrafts();
	const mayWrite = canWritePrescriptionDrafts();
	const patientUuid = patient.uuid;

	const draftsQuery = useQuery({
		queryKey: prescriptionDraftsKey(patientUuid),
		queryFn: async () => {
			const drafts = await listPatientPrescriptionDrafts(patientUuid);
			// CU-14ygtkjeh2z: a list that names any other patient is unsafe as a whole, so none of it is shown.
			if (drafts.some((draft) => draft.patient_uuid !== patientUuid)) throw new ForeignPatientDraftError();
			return drafts;
		},
		enabled: mayRead && !!patientUuid,
		retry: false,
	});

	if (!mayRead) {
		return <Alert type="info" showIcon message="Your account cannot view prescription drafts." data-testid="prescriptions-not-permitted" />;
	}

	const organisationsReady = organisations.status === "ready" && organisations.organisations.length > 0;
	const loadFailure = draftsQuery.isError
		? draftsQuery.error instanceof ForeignPatientDraftError
			? "Healthcare returned drafts for a different patient, so none are shown. Close and reopen this record; if it happens again, report it."
			: classifyPrescriptionDraftFailure(draftsQuery.error).message
		: null;

	const columns = [
		{
			title: "Medicine",
			key: "medicine",
			render: (_: unknown, draft: PrescriptionDraft) => medicineSummary(draft.particulars),
		},
		{
			title: "Type",
			dataIndex: "modality",
			key: "modality",
			width: 110,
			render: (_: unknown, draft: PrescriptionDraft) => modalityLabel(draft.modality),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 170,
			render: (_: unknown, draft: PrescriptionDraft) =>
				isEditableDraft(draft) ? <StatusTag status="draft" label={DRAFT_NOT_ISSUED_LABEL} /> : <StatusTag status="unknown" label="Unrecognised status" />,
		},
		{
			title: "Last saved",
			dataIndex: "updated_at",
			key: "updated_at",
			width: 170,
			render: (_: unknown, draft: PrescriptionDraft) => (draft.updated_at ? formatInZone(draft.updated_at, "D MMM YYYY, h:mm a") : "—"),
		},
	];

	const newDraftButton = (
		<Button
			type="primary"
			icon={<PlusOutlined />}
			disabled={!organisationsReady}
			onClick={() => setModal({ mode: { kind: "create" } })}
			data-testid="prescriptions-new-draft"
		>
			New draft
		</Button>
	);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="prescriptions-tab">
			<Alert
				type="info"
				showIcon
				message="Prescription drafts only"
				description="A draft is a working record. It has not been approved, signed, issued or sent to a pharmacy, the patient or any eRx service."
			/>

			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
				<Space wrap>
					{mayWrite &&
						(organisationsReady ? (
							newDraftButton
						) : (
							<Tooltip title="A prescribing organisation must be selectable before a new draft can be saved.">
								<span>{newDraftButton}</span>
							</Tooltip>
						))}
					{mayWrite && !organisationsReady && (
						<Text type="secondary" data-testid="prescriptions-new-draft-unavailable">
							New drafts need a prescribing organisation, and none can be selected yet.
						</Text>
					)}
				</Space>
				<Button icon={<ReloadOutlined />} onClick={() => draftsQuery.refetch()} loading={draftsQuery.isFetching} data-testid="prescriptions-refresh">
					Refresh
				</Button>
			</div>

			{loadFailure ? (
				<Alert
					type="error"
					showIcon
					message={loadFailure}
					data-testid="prescriptions-load-failure"
					action={
						<Button size="small" onClick={() => draftsQuery.refetch()}>
							Try again
						</Button>
					}
				/>
			) : (
				<DataTable<PrescriptionDraft>
					rowKey="uuid"
					columns={columns}
					dataSource={draftsQuery.data ?? []}
					loading={draftsQuery.isLoading}
					onRowClick={(draft) => setModal({ mode: { kind: "view", uuid: draft.uuid }, draft })}
					emptyTitle="No prescription drafts"
					emptyDescription="Drafts you create for this patient appear here. Only the prescriber who wrote a draft can see it."
					pagination={false}
					data-testid="prescriptions-draft-list"
				/>
			)}

			{modal && (
				<PrescriptionDraftModal patient={patient} initialMode={modal.mode} initialDraft={modal.draft} organisations={organisations} onClose={() => setModal(null)} />
			)}
		</div>
	);
};

export default PrescriptionsTab;
